import { InfoPopover, RiSearchLine, Stack } from 'component-library';
import { Trans, useTranslation } from 'react-i18next';

import {
  BenchmarkType,
  DegradationDriver,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { CapsuleTrend } from '@/components';
import { GraphFact } from '@/components/Charts/types';
import { NCCardSectionHeader, printLatestDate, printNthYearDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { DeforestationBenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { assertBenchmarkType } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

export type DeforestationCardStatsTileProps = {
  latestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  oneYearUpliftFact: PlotReportFactElement<number | null> | undefined;
  oneYearPctChangeFact: PlotReportFactElement<number | null> | undefined;
  benchmarkFact: PlotReportFactElement<GraphFact[] | null> | undefined;
  dominantDriver: PlotReportFactElement | undefined;
  secondaryDriver: PlotReportFactElement | undefined;
};

export const DeforestationCardStatsTile = ({
  latestAnalysisFact,
  oneYearUpliftFact,
  oneYearPctChangeFact,
  benchmarkFact,
  dominantDriver,
  secondaryDriver,
}: DeforestationCardStatsTileProps) => {
  const { t } = useTranslation();

  const deforestationTrendDisplay = !isNullish(oneYearPctChangeFact?.value)
    ? `${getDisplayNumber(oneYearPctChangeFact?.value ?? 0, undefined, {
        signDisplay: 'always',
      })} %`
    : '-';

  const latestAnalysisDisplay = !isNullish(latestAnalysisFact?.value)
    ? // eslint-disable-next-line sonarjs/no-nested-template-literals
      `${getDisplayNumber(latestAnalysisFact?.value ?? 0)} ${t(`global.ui.unit.${latestAnalysisFact?.unit ?? UnitEnum.none}`)}`
    : '-';

  const lastMeasuredDate = printLatestDate([latestAnalysisFact?.measured_at]);
  const oneYearChangePreviousMeasuredAt = printNthYearDate([oneYearUpliftFact?.measured_at], -1);

  const benchmkarks =
    benchmarkFact?.value?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) ?? [];

  const benchmarkType = benchmkarks
    ?.at(-1)
    ?.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.benchmark)?.value as BenchmarkType | undefined;

  assertBenchmarkType(benchmarkType, 'DeforestationCardStatsTile', BenchmarkType.very_high);

  const dominantDriverName =
    (dominantDriver?.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.deforestation_driver)
      ?.value as keyof typeof DegradationDriver) ?? DegradationDriver.no_data;
  const dominantDriverNameDisplay = t(
    `shared.ncaDetail.details.deforestation.degradationDrivers.drivers.${dominantDriverName as keyof typeof DegradationDriver}`,
  );
  const dominantDriverValueDisplay = !isNullish(dominantDriver?.value)
    ? `${getDisplayNumber((dominantDriver?.value as number) ?? 0)}${t('global.ui.unit.%')}`
    : '';
  const secondaryDriverName =
    (secondaryDriver?.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.deforestation_driver)
      ?.value as keyof typeof DegradationDriver) ?? DegradationDriver.no_data;
  const secondaryDriverNameDisplay = t(
    `shared.ncaDetail.details.deforestation.degradationDrivers.drivers.${secondaryDriverName as keyof typeof DegradationDriver}`,
  );
  const secondaryDriverValueDisplay = !isNullish(secondaryDriver?.value)
    ? `${getDisplayNumber((secondaryDriver?.value as number) ?? 0)}${t('global.ui.unit.%')}`
    : '';

  return (
    <Stack className='justify-center gap-8 p-6 lg:gap-4 lg:p-8 lg:pr-2' data-testid='deforestation-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.deforestation.labels.deforestationStats')}
          supplementaryInfo={`${t('shared.ncaDetail.details.labels.updated')} ${lastMeasuredDate}`}
          popover={{
            title: t('shared.ncaDetail.details.deforestation.explainers.deforestation.title'),
            body: t('shared.ncaDetail.details.deforestation.explainers.deforestation.body'),
          }}
        />
        <Stack direction='row' className='flex-wrap gap-6'>
          <NCFactCapsule
            label={t('shared.ncaDetail.details.labels.latestAnalysis')}
            fact={latestAnalysisFact}
            trend={
              typeof oneYearPctChangeFact?.value === 'number' && (
                <CapsuleTrend variant={oneYearPctChangeFact.value < 0 ? 'positive' : 'negative'}>
                  {deforestationTrendDisplay}
                </CapsuleTrend>
              )
            }
            capsuleIconProps={{
              icon: getNCChangeIcon(latestAnalysisFact),
              variant: getNCChangeIconVariant(latestAnalysisFact),
            }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
              body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', { date: lastMeasuredDate }),
              popoverTriggerProps: {
                className: 'text-xs',
              },
            }}
          />
          {!isNullish(oneYearUpliftFact?.value) && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.labels.oneYearChange')}
              fact={oneYearUpliftFact}
              capsuleIconProps={{
                icon: getNCChangeIcon(oneYearUpliftFact),
                variant: getNCChangeIconVariant(oneYearUpliftFact),
              }}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.deforestation.explainers.oneYearChange.title'),
                body: t('shared.ncaDetail.details.deforestation.explainers.oneYearChange.body', {
                  date: lastMeasuredDate,
                  previousDate: oneYearChangePreviousMeasuredAt,
                }),
                popoverTriggerProps: {
                  'data-testid': 'deforestation-since-previous-analysis-tooltip',
                },
              }}
            />
          )}
          {benchmarkType && (
            <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
              <DeforestationBenchmarkPill benchmarkType={benchmarkType} />
              <span className='typography-body1Bold'>
                {t('shared.ncaDetail.details.deforestation.labels.whatDoesThisMean')}
              </span>
              <Trans
                i18nKey={`shared.ncaDetail.details.deforestation.explainers.benchmark.${benchmarkType}`}
                tOptions={{ value: latestAnalysisDisplay }}
                components={{
                  p: <p className='typography-body1 text-text-secondary' />,
                }}
              />
            </Stack>
          )}
        </Stack>
        {(!isNullish(dominantDriver?.value) || !isNullish(secondaryDriver?.value)) && (
          <>
            <NCCardSectionHeader
              title={t('shared.ncaDetail.details.deforestation.degradationDrivers.labels.heading')}
              popover={{
                title: t('shared.ncaDetail.details.deforestation.degradationDrivers.labels.heading'),
                body: t('shared.ncaDetail.details.deforestation.degradationDrivers.explainers.howDoWeMeasure.body'),
              }}
            />
            <Stack className='flex flex-col justify-start gap-8'>
              {!isNullish(dominantDriver?.value) && (
                <NCFactCapsule
                  label={`${t('shared.ncaDetail.details.deforestation.degradationDrivers.driverTypes.dominant')} - ${
                    dominantDriverValueDisplay
                  }`}
                  fact={dominantDriver}
                  capsuleIconProps={{
                    icon: <RiSearchLine style={{ color: '#c0880e' }} />,
                    variant: 'default',
                    className: 'bg-transparent border border-neutral-black-8',
                  }}
                >
                  <Stack className='flex flex-row justify-start gap-4'>
                    <span className='typography-body1 text-text-primary'>{dominantDriverNameDisplay}</span>
                    <InfoPopover
                      popoverTriggerProps={{ className: 'text-text-secondary' }}
                      body={t(
                        `shared.ncaDetail.details.deforestation.degradationDrivers.explainers.whatDoesThisMean.drivers.${dominantDriverName as keyof typeof DegradationDriver}`,
                      )}
                    />
                  </Stack>
                </NCFactCapsule>
              )}
              {!isNullish(secondaryDriver?.value) && (
                <NCFactCapsule
                  label={`${t('shared.ncaDetail.details.deforestation.degradationDrivers.driverTypes.secondary')} - ${
                    secondaryDriverValueDisplay
                  }`}
                  fact={secondaryDriver}
                  capsuleIconProps={{
                    icon: <RiSearchLine style={{ color: '#c0880e' }} />,
                    variant: 'default',
                    className: 'bg-transparent border border-neutral-black-8',
                  }}
                >
                  <Stack className='flex flex-row justify-start gap-4'>
                    <span className='typography-body1 text-text-primary'>{secondaryDriverNameDisplay}</span>
                    <InfoPopover
                      popoverTriggerProps={{ className: 'text-text-secondary' }}
                      body={t(
                        `shared.ncaDetail.details.deforestation.degradationDrivers.explainers.whatDoesThisMean.drivers.${secondaryDriverName as keyof typeof DegradationDriver}`,
                      )}
                    />
                  </Stack>
                </NCFactCapsule>
              )}
            </Stack>
          </>
        )}
      </div>
    </Stack>
  );
};
